// Stillhouse view — Teams & users. Members + teams are wired to /api/fleet.
import React, { useEffect, useState } from "react";
import { C, F, Btn } from "../brand/apo";
import { Card, SectionLabel, StatusDot, Chip, Avatar, Tabs, PageHeader, EmptyState } from "../brand/sh";
import { api, type FleetUser, type Team } from "../api";

const Scroll = ({ children }: { children: React.ReactNode }) => (
  <div style={{ flex: 1, overflow: "auto", padding: "22px 32px", display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
);

function isAdminRole(roleKey: string) {
  const k = roleKey.toUpperCase();
  return k === "OWNER" || k === "ADMIN";
}

export function TeamsUsers() {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState<FleetUser[] | null>(null);
  const [teams, setTeams] = useState<Team[] | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .getFleetUsers()
      .then((r) => alive && setUsers(r.users))
      .catch(() => alive && setUsers([]));
    api
      .getTeams()
      .then((r) => alive && setTeams(r.teams))
      .catch(() => alive && setTeams([]));
    return () => {
      alive = false;
    };
  }, []);

  const ulist = users ?? [];
  const tlist = teams ?? [];
  const teamMemberCount = (id: string) => ulist.filter((u) => u.teamId === id).length;

  return (
    <React.Fragment>
      <PageHeader
        crumb="PEOPLE › DIRECTORY"
        title="Teams & users"
        sub="Team-based organisation synced from your IdP. Roles, group membership and machine access all roll up to the team."
      >
        <Btn ghost>Create team</Btn>
        <Btn primary>Invite member</Btn>
      </PageHeader>
      <div style={{ padding: "0 32px", borderBottom: `1px solid ${C.line}`, background: C.inkHi }}>
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { id: "users", label: "Members", count: ulist.length },
            { id: "teams", label: "Teams", count: tlist.length },
            { id: "groups", label: "Groups", count: 0 },
          ]}
        />
      </div>
      <Scroll>
        {tab === "users" && (
          <Card pad={false}>
            <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr 1fr", gap: 14, padding: "11px 18px", fontFamily: F.mono, fontSize: 10, letterSpacing: "0.16em", color: C.fgFaint, fontWeight: 600, borderBottom: `1px solid ${C.line}`, background: C.inkHi }}>
              <span>MEMBER</span>
              <span>ROLE</span>
              <span>TEAM</span>
              <span>STATUS</span>
            </div>
            {ulist.length === 0 && <EmptyState title="No members yet" sub="INVITE A MEMBER TO GET STARTED" />}
            {ulist.map((u, i) => (
              <div key={u.id} style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr 1fr", gap: 14, padding: "12px 18px", borderBottom: i < ulist.length - 1 ? `1px solid ${C.line}` : "none", alignItems: "center", fontFamily: F.ui, fontSize: 13 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 11 }}>
                  <Avatar name={u.displayName} size={32} />
                  <span>
                    <span style={{ color: C.fgDark, fontWeight: 500 }}>{u.displayName}</span>
                    <div style={{ fontFamily: F.mono, fontSize: 10.5, color: C.fgFaint, marginTop: 2 }}>{u.email}</div>
                  </span>
                </span>
                <span>{isAdminRole(u.roleKey) ? <Chip color={C.glow} solid>{u.roleKey}</Chip> : <Chip color={C.fgMute}>{u.roleKey}</Chip>}</span>
                <span style={{ color: C.fgMute }}>{u.teamName ?? "—"}</span>
                <StatusDot status={u.status.toLowerCase()} />
              </div>
            ))}
          </Card>
        )}
        {tab === "teams" && (
          tlist.length === 0 ? (
            <Card pad={false}>
              <EmptyState title="No teams yet" sub="CREATE A TEAM TO ORGANISE MEMBERS" />
            </Card>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 16 }}>
              {tlist.map((t) => (
                <Card
                  key={t.id}
                  title={t.name}
                  tag={<Chip color={t.color ?? C.fgMute}>{teamMemberCount(t.id)} members</Chip>}
                  action={
                    <Btn ghost size="md" style={{ padding: "5px 12px", fontSize: 12 }}>
                      Manage
                    </Btn>
                  }
                >
                  <div style={{ display: "flex", gap: 28 }}>
                    <div>
                      <SectionLabel>Members</SectionLabel>
                      <div style={{ fontFamily: F.display, fontSize: 26, fontWeight: 500, marginTop: 4 }}>
                        {teamMemberCount(t.id)}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )
        )}
        {tab === "groups" && (
          <Card pad={false}>
            <EmptyState title="No groups yet" sub="GROUPS SYNCED FROM YOUR IDP APPEAR HERE" />
          </Card>
        )}
      </Scroll>
    </React.Fragment>
  );
}
